import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  RawCase,
  RawChat,
  RawFile,
  RawFileMeta,
  RawKnowledge,
  RawSource,
  RawState,
  RawStateIndexEntry,
} from './demo-export.types';

/**
 * The exported data of a single demo case, plus the lookups the request handlers need.
 *
 * Loading is split the same way the export is: {@link load} fetches the small manifests that
 * every page of a case needs, while file meta (document text) and chat states (message
 * history) are fetched on first use and cached. A demo case with a hundred documents would
 * otherwise cost tens of megabytes to open.
 */
export class DemoCaseBundle {
  readonly filesById = new Map<string, RawFile>();
  readonly childFileIdsByParent = new Map<string | null, string[]>();
  readonly chatsById = new Map<string, RawChat>();
  readonly chatByStateId = new Map<string, RawChat>();
  readonly sourcesById = new Map<string, RawSource>();
  readonly stateIndexById = new Map<string, RawStateIndexEntry>();
  /** Sub-states keyed by the message that spawned them (a message's `child_messages`). */
  readonly stateIdsByParentMessageId = new Map<string, string[]>();

  private readonly fileMetaCache = new Map<string, Promise<RawFileMeta>>();
  private readonly stateCache = new Map<string, Promise<RawState>>();

  private constructor(
    readonly caseId: string,
    readonly baseUrl: string,
    private readonly http: HttpClient,
    readonly caseData: RawCase,
    readonly files: RawFile[],
    readonly chats: RawChat[],
    readonly sources: RawSource[],
    readonly knowledge: RawKnowledge,
    stateIndex: RawStateIndexEntry[],
  ) {
    for (const file of files) {
      this.filesById.set(file.id, file);
      const siblings = this.childFileIdsByParent.get(file.parent_file);
      if (siblings) {
        siblings.push(file.id);
      } else {
        this.childFileIdsByParent.set(file.parent_file, [file.id]);
      }
    }

    for (const chat of chats) {
      this.chatsById.set(chat.id, chat);
      this.chatByStateId.set(chat.associate_state_id, chat);
    }

    for (const source of sources) {
      this.sourcesById.set(source.id, source);
    }

    for (const entry of stateIndex) {
      this.stateIndexById.set(entry.id, entry);
      if (!entry.parent_message_id) continue;
      const spawned = this.stateIdsByParentMessageId.get(entry.parent_message_id);
      if (spawned) {
        spawned.push(entry.id);
      } else {
        this.stateIdsByParentMessageId.set(entry.parent_message_id, [entry.id]);
      }
    }
  }

  static async load(http: HttpClient, caseId: string, baseUrl: string): Promise<DemoCaseBundle> {
    const get = <T>(name: string) => firstValueFrom(http.get<T>(`${baseUrl}/${name}`));
    const [caseData, files, chats, sources, knowledge, stateIndex] = await Promise.all([
      get<RawCase>('case.json'),
      get<RawFile[]>('files.json'),
      get<RawChat[]>('chats.json'),
      get<RawSource[]>('sources.json'),
      get<RawKnowledge>('knowledge.json'),
      get<RawStateIndexEntry[]>('states.json'),
    ]);

    return new DemoCaseBundle(
      caseId,
      baseUrl,
      http,
      caseData,
      files,
      chats,
      sources,
      knowledge,
      stateIndex,
    );
  }

  /** Raw content URL of a file, mirroring `local_path` in the export. */
  fileContentUrl(fileId: string): string {
    return `${this.baseUrl}/files/${fileId}`;
  }

  fileMeta(fileId: string): Promise<RawFileMeta> {
    const cached = this.fileMetaCache.get(fileId);
    if (cached) return cached;

    const pending = firstValueFrom(
      this.http.get<RawFileMeta>(`${this.baseUrl}/meta/${fileId}.json`),
    ).catch((error) => {
      // A folder (or a file whose analysis never produced snippets) has no meta document.
      // Callers only ever want the derived extras, so an empty one is the honest answer —
      // but don't cache the failure, a transient network error should be retryable.
      this.fileMetaCache.delete(fileId);
      if (error?.status === 403 || error?.status === 404) {
        return { id: fileId, text_snippets: [], entities: [], entity_ref: null };
      }
      throw error;
    });

    this.fileMetaCache.set(fileId, pending);
    return pending;
  }

  state(stateId: string): Promise<RawState> {
    const cached = this.stateCache.get(stateId);
    if (cached) return cached;

    const pending = firstValueFrom(
      this.http.get<RawState>(`${this.baseUrl}/states/${stateId}.json`),
    ).catch((error) => {
      this.stateCache.delete(stateId);
      throw error;
    });

    this.stateCache.set(stateId, pending);
    return pending;
  }

  /** Loads the states spawned from any of `messageIds`, skipping ones the case has none for. */
  async childStates(messageIds: readonly string[]): Promise<RawState[]> {
    const stateIds = messageIds.flatMap((id) => this.stateIdsByParentMessageId.get(id) ?? []);
    if (stateIds.length === 0) return [];
    return Promise.all(stateIds.map((id) => this.state(id)));
  }

  childFileIds(parentFileId: string | null): readonly string[] {
    return this.childFileIdsByParent.get(parentFileId) ?? [];
  }
}
